// Indemnités journalières (IJ) maladie d'un salarié du privé, arrêt non
// professionnel. Sources : ameli.fr (page du 01/09/2026) et service-public.gouv.fr
// F3053 (01/06/2026).
//
// IJ brute = 50 % du salaire journalier de base (SJB) ; SJB = salaires bruts des
// 3 derniers mois ÷ 91,25, dans la limite de 1,4 SMIC mensuel (arrêts débutant
// depuis le 01/04/2025). Délai de carence de 3 jours. CSG 6,2 % + CRDS 0,5 %.
// À mettre à jour à chaque revalorisation du SMIC.

export const CARENCE_SECU = 3;
export const CARENCE_EMPLOYEUR = 7;
export const CSG_CRDS = 0.067;
export const IJ_RATE = 0.5;

// Plafond mensuel : 1,4 × le SMIC mensuel en vigueur le dernier jour du mois
// civil précédant l'arrêt (art. R323-4 CSS). SMIC : 1 801,80 € (2025),
// 1 823,03 € (01/01/2026), 1 867,02 € (01/06/2026). D'où, pour un arrêt débutant :
// - de janvier 2026 : SMIC du 31/12/2025, plafond 2 522,52 €, IJ max 41,47 € ;
// - de février à juin 2026 : 2 552,24 €, IJ max 41,95 € (montant ameli pour juin) ;
// - à partir de juillet 2026 : 2 613,83 €, IJ max 42,97 € (ameli).
// La page ameli du 01/09/2026 indique 2 522,52 € pour juin : c'est le chiffre de
// 2025, incohérent avec l'IJ maximale de 41,95 € qu'elle donne pour ce même mois.
export const CAPS = [
  { from: "2026-07-01", cap: 2613.83 },
  { from: "2026-02-01", cap: 2552.24 },
  { from: "2025-04-01", cap: 2522.52 },
];

export function capFor(dateIso) {
  const c = CAPS.find((c) => dateIso >= c.from);
  return c ? c.cap : null;
}

const cents = (x) => Math.round(x * 100) / 100;

export function dailyIj(monthlyGross, dateIso) {
  const cap = capFor(dateIso);
  if (cap === null || !(monthlyGross > 0)) return null;
  const sjb = (Math.min(monthlyGross, cap) * 3) / 91.25;
  const brute = cents(sjb * IJ_RATE);
  return { sjb, brute, nette: cents(brute * (1 - CSG_CRDS)), capped: monthlyGross > cap, cap };
}

// Maintien de salaire légal (Code du travail, loi de mensualisation) : 1 an
// d'ancienneté minimum, carence de 7 jours, 90 % puis 66,66 % de la rémunération
// brute, IJ comprises. Durée de chaque période : 30 jours, + 10 jours par
// tranche entière de 5 ans d'ancienneté au-delà de la première année, 90 jours au plus.
export function employerPeriodDays(seniorityYears) {
  if (!(seniorityYears >= 1)) return 0;
  return Math.min(90, 30 + 10 * Math.floor((seniorityYears - 1) / 5));
}

// Détail jour par jour, puis totaux. Salaire journalier de référence de
// l'employeur : salaire mensuel ÷ 30 (estimation).
export function simulate({ monthlyGross, dateIso, days, seniorityYears = 0, prolongation = false }) {
  const ij = dailyIj(monthlyGross, dateIso);
  if (!ij || !(days >= 1)) return null;
  const period = employerPeriodDays(seniorityYears);
  const dailySalary = monthlyGross / 30;
  const carenceSecu = prolongation ? 0 : CARENCE_SECU;
  const carenceEmployeur = prolongation ? 0 : CARENCE_EMPLOYEUR;

  let ijDays = 0, full = 0, twoThirds = 0, complement = 0;
  for (let d = 1; d <= days; d++) {
    const ijToday = d > carenceSecu ? ij.brute : 0;
    if (ijToday) ijDays++;
    const k = d - carenceEmployeur; // rang du jour indemnisé par l'employeur
    let level = 0;
    if (period > 0 && k >= 1) {
      if (k <= period) { level = 0.9; full++; }
      else if (k <= 2 * period) { level = 2 / 3; twoThirds++; }
    }
    if (level) complement += Math.max(0, level * dailySalary - ijToday);
  }

  const ijBruteTotal = cents(ij.brute * ijDays);
  const salaryTotal = cents(dailySalary * days);
  const complementTotal = cents(complement);
  return {
    ij,
    period,
    ijDays,
    daysAt90: full,
    daysAt66: twoThirds,
    ijBruteTotal,
    ijNetteTotal: cents(ij.nette * ijDays),
    complementTotal,
    receivedGross: cents(ijBruteTotal + complementTotal),
    salaryTotal,
    lossGross: cents(salaryTotal - ijBruteTotal - complementTotal),
  };
}
